/**
 * Product Card Add to Cart Component
 *
 * Handles size selection and variant updates for product cards.
 * Listens to size-selector component changes via pub/sub.
 * No Shadow DOM.
 */

if (!customElements.get('product-card-atc')) {
  customElements.define(
    'product-card-atc',
    class ProductCardAtc extends HTMLElement {
      abortController = undefined;

      constructor() {
        super();
        this.form = null;
        this.variantSelect = null;
        this.sizeSelector = null;
        this.sizeInputs = [];
        this.addToCartButton = null;
        this.ukSizeInput = null;
        this.currentSizeDisplay = null;
        this.currentUnit = 'eu'; // Default to EU
        this.unsubscribeVariantChange = null;
      }

      connectedCallback() {
        this.abortController = new AbortController();
        this.initElements();
        this.initEventListeners();
        this.subscribeToPubSub();
        this.initializeVariantSelect();
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.unsubscribeVariantChange?.();
      }

      initElements() {
        this.form = this.querySelector('form[data-type="add-to-cart-form"]');
        this.variantSelect = this.querySelector('[data-variant-select]');
        this.sizeSelector = this.querySelector('size-selector');
        this.addToCartButton = this.querySelector('[data-add-to-cart-button]');
        this.ukSizeInput = this.querySelector('[data-uk-size-input]');
        this.currentSizeDisplay = this.querySelector('[data-current-size]');

        // Get size inputs - they might be inside size-selector component
        this.sizeInputs = [];
        if (this.sizeSelector) {
          this.sizeInputs = Array.from(this.sizeSelector.querySelectorAll('input[type="radio"][data-variant-id]'));
        }
      }

      initEventListeners() {
        // Size input change handlers - listen directly on the inputs
        this.sizeInputs.forEach(input => {
          input.addEventListener(
            'change',
            this.handleSizeChange.bind(this),
            { signal: this.abortController.signal }
          );
        });

        // Also listen to click events on labels (in case change doesn't fire)
        if (this.sizeSelector) {
          const labels = this.sizeSelector.querySelectorAll('label.size-selector__button');
          labels.forEach(label => {
            label.addEventListener(
              'click',
              this.handleLabelClick.bind(this),
              { signal: this.abortController.signal }
            );
          });

          // Listen to EU/UK toggle buttons
          const unitButtons = this.sizeSelector.querySelectorAll('[data-unit]');
          unitButtons.forEach(button => {
            button.addEventListener(
              'click',
              this.handleUnitToggle.bind(this),
              { signal: this.abortController.signal }
            );
          });
        }

        // Form submit handler
        if (this.form) {
          this.form.addEventListener(
            'submit',
            //this.handleSubmit.bind(this),
            { signal: this.abortController.signal }
          );
        }
      }

      /**
       * Initialize variant select with checked radio input value
       */
      initializeVariantSelect() {
        if (!this.sizeSelector || !this.variantSelect) return;

        // Remove form attribute from all radio inputs to prevent them from submitting
        // We only want the variant ID from the select element
        this.sizeInputs.forEach(input => {
          input.removeAttribute('form');
        });

        // Check if there's a checked radio input
        const checkedInput = this.sizeSelector.querySelector('input[type="radio"]:checked');

        if (checkedInput && checkedInput.dataset.variantId) {
          // Update the select with the checked variant
          this.variantSelect.value = checkedInput.dataset.variantId;

          // Also update button state and UK size
          this.updateAddToCartButtonFromInput(checkedInput);
          this.updateUkSize(checkedInput);
          this.updateCurrentSizeDisplay(checkedInput);

          // Update selected class
          const label = checkedInput.nextElementSibling;
          if (label && label.classList.contains('size-selector__button')) {
            this.updateSelectedClass(label);
          }
        } else {
          // No size selected - ensure button shows "Größe auswählen"
          if (this.variantSelect.options.length > 0) {
            // Keep the first option selected as default, but button will show "Größe auswählen"
            this.variantSelect.selectedIndex = 0;
          }
        }
      }

      /**
       * Subscribe to pub/sub events
       */
      subscribeToPubSub() {
        if (!window.FoxTheme?.pubsub) return;

        // Listen for size changes from size-selector component
        this.unsubscribeVariantChange = window.FoxTheme.pubsub.subscribe(
          window.FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleVariantChangeEvent.bind(this)
        );
      }

      /**
       * Handle variant change event from pub/sub
       */
      handleVariantChangeEvent(event) {
        const { target } = event.data;

        // Only handle events from our size-selector
        if (!this.sizeSelector || !this.sizeSelector.contains(target)) {
          return;
        }

        const variantId = target.dataset.variantId;
        if (variantId) {
          this.updateVariantSelect(variantId);
          this.updateAddToCartButtonFromInput(target);
        }
      }

      /**
       * Handle unit toggle (EU/UK)
       */
      handleUnitToggle(event) {
        const button = event.currentTarget;
        const unit = button.dataset.unit;

        if (unit && unit !== this.currentUnit) {
          this.currentUnit = unit;
          this.updateCurrentSizeDisplay();
        }
      }

      /**
       * Handle label click
       */
      handleLabelClick(event) {
        const label = event.currentTarget;
        const inputId = label.getAttribute('for');

        // Find the input within THIS component's size selector (not globally)
        const input = inputId && this.sizeSelector ?
          this.sizeSelector.querySelector(`#${CSS.escape(inputId)}`) :
          null;

        if (!input || !input.dataset.variantId) return;

        // Uncheck all other radios in the same group (only within this component)
        this.sizeInputs.forEach(radio => {
          if (radio !== input) {
            radio.checked = false;
          }
        });

        // Manually check the radio input (since form attribute is removed)
        input.checked = true;

        // Update everything
        this.updateVariantSelect(input.dataset.variantId);
        this.updateAddToCartButtonFromInput(input);
        this.updateSelectedClass(label);
        this.updateUkSize(input);
        this.updateCurrentSizeDisplay(input);
      }

      /**
       * Handle size input change
       */
      handleSizeChange(event) {
        const input = event.currentTarget;

        // Verify this input belongs to this component's size selector
        if (!input || !this.sizeSelector || !this.sizeSelector.contains(input)) {
          return;
        }

        const variantId = input.dataset.variantId;
        if (!variantId) return;

        this.updateVariantSelect(variantId);
        this.updateAddToCartButtonFromInput(input);
        this.updateUkSize(input);
        this.updateCurrentSizeDisplay(input);

        // Update selected class on the label
        const label = input.nextElementSibling;
        if (label && label.classList.contains('size-selector__button')) {
          this.updateSelectedClass(label);
        }
      }

      /**
       * Update UK size hidden input
       */
      updateUkSize(input) {
        if (!this.ukSizeInput) return;

        const ukSize = input.dataset.ukSize;
        if (ukSize) {
          this.ukSizeInput.value = ukSize;
        }
      }

      /**
       * Update current size display
       */
      updateCurrentSizeDisplay(input = null) {
        if (!this.currentSizeDisplay) return;

        // If no input provided, find the checked input
        if (!input) {
          input = this.sizeSelector?.querySelector('input[type="radio"]:checked');
        }

        if (!input) return;

        const euSize = input.dataset.euSize;
        const ukSize = input.dataset.ukSize;

        // Display EU or UK size based on current unit
        const displaySize = this.currentUnit === 'uk' && ukSize ? ukSize : euSize;

        if (displaySize) {
          this.currentSizeDisplay.textContent = displaySize;
        }
      }

      /**
       * Update selected class on size buttons
       */
      updateSelectedClass(selectedLabel) {
        if (!this.sizeSelector) return;

        // Remove 'selected' class from all labels
        const allLabels = this.sizeSelector.querySelectorAll('.size-selector__button');
        allLabels.forEach(label => {
          label.classList.remove('selected');
        });

        // Add 'selected' class to the clicked label
        if (selectedLabel) {
          selectedLabel.classList.add('selected');
        }
      }

      /**
       * Update variant select value
       */
      updateVariantSelect(variantId) {
        if (!this.variantSelect) return;

        // Convert to string for comparison (select values are always strings)
        const variantIdString = String(variantId);

        // Check if this option exists in the select
        const optionExists = Array.from(this.variantSelect.options).some(opt => opt.value === variantIdString);

        if (!optionExists) return;

        this.variantSelect.value = variantIdString;
        this.variantSelect.dispatchEvent(new Event('change', { bubbles: true }));
      }

      /**
       * Update add to cart button from input element
       */
      updateAddToCartButtonFromInput(input) {
        if (!this.addToCartButton) return;

        const isAvailable = input.dataset.available === 'true';
        const defaultText = this.addToCartButton.querySelector('[data-button-text-default]');
        const selectedText = this.addToCartButton.querySelector('[data-button-text-selected]');
        const ariaLabel = this.addToCartButton.getAttribute('aria-label');
        const productTitle = ariaLabel ? ariaLabel.split(' für ')[1] : '';

        this.addToCartButton.disabled = !isAvailable;

        if (defaultText && selectedText) {
          if (isAvailable) {
            // Show "In den Warenkorb" text
            defaultText.hidden = true;
            selectedText.hidden = false;
            selectedText.textContent = 'In den Warenkorb';
            this.addToCartButton.setAttribute('aria-label', `In den Warenkorb für ${productTitle}`);
          } else {
            // Show "Ausverkauft" in selected text span
            const soldOutText = window.FoxTheme?.translations?.soldOut || 'Ausverkauft';
            defaultText.hidden = true;
            selectedText.hidden = false;
            selectedText.textContent = soldOutText;
            this.addToCartButton.setAttribute('aria-label', `${soldOutText} - ${productTitle}`);
          }
        }
      }

      /**
       * Get sections to render on cart update
       */
      getSectionsToRender() {
        return ['cart-drawer', 'cart-icon-bubble'];
      }

      /**
       * Handle cart error
       */
      handleCartError(error) {
        console.error('Cart error:', error);

        // Publish error event if pub/sub available
        if (window.FoxTheme?.pubsub) {
          window.FoxTheme.pubsub.publish(
            window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartError,
            {
              message: error.description || error.message,
              source: 'product-card'
            }
          );
        }

        // Show error message to user
        // You can customize this based on your theme's error handling
        alert(error.description || error.message || 'Ein Fehler ist aufgetreten');
      }
    }
  );
}
